import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { NumberField } from 'components/common/NumberField';
import { CompactRequestEntry } from 'components/request/CompactRequestEntry';
import { useFirebase } from 'contexts/FirebaseContext';
import { doc, getDoc } from 'firebase/firestore';
import { useLiveEvent } from 'hooks/useLiveEvent';
import { useLocalDatabase } from 'hooks/useLocalDatabase';
import { useUser } from 'hooks/useUser';
import { EventOccasion } from 'models/EventOccasion';
import { LiveEventWish, LiveTrackResult } from 'models/LiveEvent';
import { LocalTrack } from 'models/LocalTrack';
import { Track } from 'models/Track';
import { TrackRequest, TrackRequestType } from 'models/TrackRequest';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { cleanArtistTitle } from 'utils/cleanArtistTitle';
import { isElectron } from 'utils/isElectron';
import { areKeysCompatible, parseKey } from 'utils/keyUtils';

type DisplayMode = 'all' | 'key' | 'bpm' | 'key_bpm';
type KeyFilterMode = 'exact' | 'extended';

export function EventLiveMode() {
  const { t } = useTranslation();

  // Eigenständiges Laden der Daten (wie in EventDetails)
  const { eventId } = useParams();
  const { user } = useUser();
  const db = useFirebase().db;
  const [event, setEvent] = useState<EventOccasion>();
  const [liveWishes, setLiveWishes] = useState<LiveEventWish[]>([]);
  const { listenToLiveWishes, listenToSearches, answerSearch } = useLiveEvent();
  const { localDatabase } = useLocalDatabase();
  const processedSearchesRef = useRef<Set<string>>(new Set());

  const [requests, setRequests] = useState<TrackRequest[]>([]);

  // Asynchronously convert live wishes into TrackRequest format, loading localTrack details (path, bpm, key) from SQLite
  useEffect(() => {
    let isMounted = true;

    const convertWishesToRequests = async () => {
      const mapped = await Promise.all(
        liveWishes.map(async (wish) => {
          const rawLocalId =
            typeof wish.localId === 'number'
              ? wish.localId
              : typeof wish.track?.localId === 'number'
              ? wish.track.localId
              : typeof wish.track?.id === 'number'
              ? (wish.track.id as number)
              : undefined;

          let localTrack: LocalTrack | undefined = undefined;

          if (rawLocalId != null && localDatabase) {
            try {
              const dbTrack = await localDatabase.getTrackById(rawLocalId);
              if (dbTrack && dbTrack.path) {
                localTrack = {
                  id: dbTrack.id,
                  path: dbTrack.path,
                  title: dbTrack.title || wish.track.title,
                  artist: dbTrack.artist || wish.track.artist,
                  album: dbTrack.album || wish.track.album,
                  genre: dbTrack.genre || wish.track.genre,
                  bpm: dbTrack.bpm ?? wish.track.bpm,
                  key: dbTrack.key || wish.track.key,
                  collection: dbTrack.album || wish.track.album || '',
                };
              }
            } catch (err) {
              console.error('Failed to get track from local DB by id:', rawLocalId, err);
            }
          }

          // Fallback if not fetched from db directly but wish.track has details/path
          if (!localTrack && rawLocalId != null && wish.track?.path) {
            localTrack = {
              id: rawLocalId,
              path: wish.track.path,
              title: wish.track.title,
              artist: wish.track.artist,
              album: wish.track.album,
              genre: wish.track.genre,
              bpm: wish.track.bpm,
              key: wish.track.key,
              collection: wish.track.album || '',
            };
          }

          return {
            id: wish.id,
            hash: wish.id,
            date: wish.date,
            type: TrackRequestType.Library,
            value: {
              title: localTrack?.title || wish.track.title,
              artist: localTrack?.artist || wish.track.artist,
              collection: localTrack?.album || wish.track.album,
            },
            localTrack,
            localId: rawLocalId,
          } as TrackRequest;
        })
      );

      if (isMounted) {
        setRequests(mapped);
      }
    };

    convertWishesToRequests();

    return () => {
      isMounted = false;
    };
  }, [liveWishes, localDatabase]);

  const loadEvent = async () => {
    if (eventId && user?.uid) {
      const eventRef = doc(db, `user/${user.uid}/events`, eventId);
      const eventSnapshot = await getDoc(eventRef);

      if (eventSnapshot.exists()) {
        setEvent({ ...eventSnapshot.data(), ...{ id: eventId } } as EventOccasion);
      }
    }
  };

  useEffect(() => {
    if (!user || !eventId) return;
    loadEvent();
  }, [db, user, eventId]);

  // Listener for live wishes in RTDB (sorted newest first)
  useEffect(() => {
    if (!user?.uid || !eventId) return;
    const unsubscribe = listenToLiveWishes(user.uid, eventId, (wishes) => {
      setLiveWishes(wishes);
    });
    return () => unsubscribe();
  }, [user?.uid, eventId, listenToLiveWishes]);

  // Listener for incoming guest searches in RTDB
  useEffect(() => {
    if (!user?.uid || !eventId || !localDatabase) return;

    const unsubscribe = listenToSearches(user.uid, eventId, async (search) => {
      if (processedSearchesRef.current.has(search.id)) return;
      processedSearchesRef.current.add(search.id);

      try {
        console.log('[LiveSearch] Received search request:', search);
        const searchTitle = search.title?.trim() || undefined;
        const searchArtist = search.artist?.trim() || undefined;

        let results = await localDatabase.findTracks({
          title: searchTitle,
          artist: searchArtist,
          limit: 10,
        });

        // Fallback search with cleaned title/artist if no results
        if ((!results || results.length === 0) && searchTitle && searchArtist) {
          const cleaned = cleanArtistTitle({ title: searchTitle, artist: searchArtist });
          results = await localDatabase.findTracks({
            title: cleaned.title || undefined,
            artist: cleaned.artist || undefined,
            limit: 10,
          });
        }

        // Direct fallback to artist-only or title-only if still empty
        if ((!results || results.length === 0) && searchArtist) {
          results = await localDatabase.findTracksByArtist(searchArtist);
        }
        if ((!results || results.length === 0) && searchTitle) {
          results = await localDatabase.findTracksByTitle(searchTitle);
        }

        console.log('[LiveSearch] Found results:', results?.length || 0);

        const cleanResults: LiveTrackResult[] = (results || []).slice(0, 10).map((t) => ({
          id: t.id,
          localId: t.id,
          title: t.title || '',
          artist: t.artist || '',
          album: t.album || '',
          genre: t.genre || '',
          bpm: t.bpm || undefined,
          key: t.key || undefined,
          path: t.path || undefined,
        }));

        await answerSearch(user.uid, eventId, search.id, cleanResults);
      } catch (err) {
        console.error('Error answering live search:', err);
      }
    });

    return () => unsubscribe();
  }, [user?.uid, eventId, localDatabase, listenToSearches, answerSearch]);

  useEffect(() => {
    if (event?.name) {
      document.title = t('liveModeWindowTitle', '{{eventName}} - dj-play.it LIVE', {
        eventName: event.name,
      });
    }
  }, [event?.name]);

  const handleExitLiveMode = async () => {
    try {
      await window.electron?.focusMainWindow();
    } catch (err) {
      console.error('Failed to focus main window:', err);
    }
    window.close();
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [hideOnDrag, setHideOnDrag] = useState(true);
  const [hiddenIndices, setHiddenIndices] = useState<number[]>([]);

  // Menu / Filter states
  const [showOptions, setShowOptions] = useState(false);
  const [displayMode, setDisplayMode] = useState<DisplayMode>('all');
  const [keyFilterMode, setKeyFilterMode] = useState<KeyFilterMode>('extended');
  const [targetKey, setTargetKey] = useState('8A');
  const [targetBpm, setTargetBpm] = useState(120);
  const [bpmTolerance, setBpmTolerance] = useState<number>(2.5);
  const [showUnknown, setShowUnknown] = useState(true);
  const [dynamicAdjustment, setDynamicAdjustment] = useState(true);
  const [filterHistory, setFilterHistory] = useState<
    { key: string; bpm: number; adjusted: boolean }[]
  >([]);

  // Auto pre-fill based on loaded tracks (only once when tracks are loaded)
  const [hasPrefilled, setHasPrefilled] = useState(false);

  useEffect(() => {
    if (hasPrefilled || requests.length === 0) return;

    const firstWithKey = requests.find((r) => r.localTrack?.key)?.localTrack?.key;
    const firstWithBpm = requests.find((r) => r.localTrack?.bpm && r.localTrack.bpm > 0)?.localTrack
      ?.bpm;

    let prefilledAny = false;
    if (firstWithKey) {
      const parsed = parseKey(firstWithKey);
      if (parsed) {
        setTargetKey(parsed.formatted);
        prefilledAny = true;
      }
    }
    if (firstWithBpm) {
      setTargetBpm(Math.round(firstWithBpm));
      prefilledAny = true;
    }

    if (prefilledAny) {
      setHasPrefilled(true);
    }
  }, [requests, hasPrefilled]);

  const displayRequests = useMemo(() => {
    return requests.map((req, idx) => {
      if (!req.localTrack) return { ...req, originalIndex: idx };

      const localTitle = req.localTrack.title;
      const localArtist = req.localTrack.artist;
      const localArtwork = req.localTrack.artworkUrl;

      const isTrackLike =
        req.type === TrackRequestType.Track || req.type === TrackRequestType.Library;

      const mergedValue: Track = {
        artist:
          localArtist || (isTrackLike ? (req.value as Track).artist : ''),
        title:
          localTitle ||
          (isTrackLike
            ? (req.value as Track).title
            : (req.value as string)),
        artworkUrl:
          localArtwork ||
          (isTrackLike ? (req.value as Track).artworkUrl : undefined),
        collection:
          req.localTrack.album ||
          req.localTrack.collection ||
          (isTrackLike ? (req.value as Track).collection : ''),
      };

      return {
        ...req,
        originalIndex: idx,
        value: mergedValue,
        type: req.type,
      };
    });
  }, [requests]);

  const filteredRequests = useMemo(() => {
    let list = displayRequests;

    // 1. Hide on drag filter
    if (hideOnDrag) {
      list = list.filter((req) => !hiddenIndices.includes((req as any).originalIndex));
    }

    // 2. Anzeigemodus (Display Mode) Key/BPM filter
    list = list.filter((req) => {
      const track = req.localTrack;
      const hasKey = !!track?.key;
      const hasBpm = !!track?.bpm && track.bpm > 0;

      const checkKey = () => {
        if (!hasKey) return showUnknown;
        return areKeysCompatible(track?.key, targetKey, keyFilterMode === 'exact');
      };

      const checkBpm = () => {
        if (!hasBpm) return showUnknown;
        const diff = Math.abs((track?.bpm || 0) - targetBpm);
        return diff <= bpmTolerance;
      };

      if (displayMode === 'key') return checkKey();
      if (displayMode === 'bpm') return checkBpm();
      if (displayMode === 'key_bpm') return checkKey() && checkBpm();
      return true; // 'all' mode
    });

    // 3. Search term filter
    if (!searchTerm) return list;
    const lowerSearch = searchTerm.toLowerCase();
    return list.filter((req) => {
      const isTrackLike =
        req.type === TrackRequestType.Track || req.type === TrackRequestType.Library;
      const title =
        (isTrackLike
          ? (req.value as Track).title
          : (req.value as string)) || '';
      const artist = (isTrackLike ? (req.value as Track).artist : '') || '';
      return (
        title.toLowerCase().includes(lowerSearch) || artist.toLowerCase().includes(lowerSearch)
      );
    });
  }, [
    displayRequests,
    searchTerm,
    hiddenIndices,
    hideOnDrag,
    displayMode,
    keyFilterMode,
    targetKey,
    targetBpm,
    bpmTolerance,
    showUnknown,
  ]);

  const handleUndoHide = () => {
    setHiddenIndices((prev) => prev.slice(0, -1));
    setFilterHistory((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.adjusted) {
        setTargetKey(last.key);
        setTargetBpm(last.bpm);
      }
      return prev.slice(0, -1);
    });
  };

  const handleResetHidden = () => {
    if (
      window.confirm(
        t('confirmShowAllHidden', 'Are you sure you want to show all hidden requests again?')
      )
    ) {
      setHiddenIndices([]);
      if (filterHistory.length > 0) {
        const first = filterHistory[0];
        if (first.adjusted) {
          setTargetKey(first.key);
          setTargetBpm(first.bpm);
        }
      }
      setFilterHistory([]);
    }
  };

  const camelotKeysList = useMemo(() => {
    const list = [];
    for (let i = 1; i <= 12; i++) {
      list.push(`${i}A`);
      list.push(`${i}B`);
    }
    return list;
  }, []);

  const closeSearch = () => {
    setShowSearch(false);
    setSearchTerm('');
  };

  if (!event) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (showOptions) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Stack alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => setShowOptions(false)}
            variant="contained"
            fullWidth
            color="secondary"
          >
            {t('backToLiveMode', 'Back to Live Mode')}
          </Button>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h3">{t('options', 'Options')}</Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showUnknown}
                      onChange={(e) => setShowUnknown(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {t('showUnknown', 'Show unmatched tracks')}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {t('showUnknownDesc', 'Include tracks with no key or BPM data')}
                      </Typography>
                    </Box>
                  }
                  sx={{ userSelect: 'none', alignItems: 'flex-start' }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={hideOnDrag}
                      onChange={(e) => setHideOnDrag(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={t('hideDraggedTracks', 'Hide tracks after dragging')}
                  sx={{ userSelect: 'none' }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={dynamicAdjustment}
                      onChange={(e) => setDynamicAdjustment(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {t('dynamicAdjustment', 'Auto-update target')}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block' }}
                      >
                        {t(
                          'dynamicAdjustmentDesc',
                          'Sets the target key and BPM to match the dragged track'
                        )}
                      </Typography>
                    </Box>
                  }
                  sx={{ userSelect: 'none', alignItems: 'flex-start' }}
                />
              </Stack>
            </CardContent>
          </Card>
          <Button
            startIcon={<RestoreIcon />}
            onClick={handleResetHidden}
            variant="outlined"
            fullWidth
            color="error"
            disabled={hiddenIndices.length === 0}
          >
            {t('restoreHiddenRequests', 'Restore requests')}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {showSearch ? (
        <TextField
          fullWidth
          autoFocus
          size="small"
          placeholder={t('filterRequests', 'Filter requests')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={closeSearch} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      ) : (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title={t('exitLiveMode', 'Close Live Mode')}>
            <IconButton onClick={handleExitLiveMode} size="small" color="error">
              <ExitToAppIcon sx={{ transform: 'scaleX(-100%)' }} />
            </IconButton>
          </Tooltip>
          <Box flexGrow={1} />

          <Tooltip title={t('undoLastHidden', 'Undo')}>
            <IconButton size="small" disabled={hiddenIndices.length === 0} onClick={handleUndoHide}>
              <UndoIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('searchRequest', 'Search request')}>
            <IconButton onClick={() => setShowSearch(true)} size="small">
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('liveModeSettings', 'Options')}>
            <IconButton onClick={() => setShowOptions(true)} size="small">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      )}

      {/* Filter Mode */}
      <ToggleButtonGroup
        value={displayMode}
        exclusive
        onChange={(_, val) => val && setDisplayMode(val)}
        fullWidth
        size="small"
        color="primary"
        sx={{ mb: 2 }}
      >
        <ToggleButton sx={{ fontSize: 12, px: 0 }} value="all">
          {t('modeAll', 'All')}
        </ToggleButton>
        <ToggleButton sx={{ fontSize: 12, px: 0 }} value="key">
          {t('modeKey', 'Key')}
        </ToggleButton>
        <ToggleButton sx={{ fontSize: 12, px: 0 }} value="bpm">
          {t('modeBpm', 'BPM')}
        </ToggleButton>
        <ToggleButton sx={{ fontSize: 12, px: 0 }} value="key_bpm">
          {t('modeKeyBpm', 'Key & BPM')}
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Key Filter */}
      {(displayMode === 'key' || displayMode === 'key_bpm') && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel id="target-key-label">{t('targetKey', 'Target Key')}</InputLabel>
            <Select
              labelId="target-key-label"
              value={targetKey}
              label={t('targetKey', 'Target Key')}
              onChange={(e) => setTargetKey(e.target.value)}
            >
              {camelotKeysList.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={keyFilterMode === 'extended'}
                onChange={(e) => setKeyFilterMode(e.target.checked ? 'extended' : 'exact')}
                color="primary"
              />
            }
            label={keyFilterMode === 'extended' ? t('extended', 'Extended') : t('exact', 'Exact')}
            sx={{ userSelect: 'none', flex: 1, minWidth: 0 }}
          />
        </Stack>
      )}

      {/* BPM Filter */}
      {(displayMode === 'bpm' || displayMode === 'key_bpm') && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          useFlexGap
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <NumberField
            min={40}
            max={250}
            value={targetBpm}
            size="small"
            label={t('targetBpm', 'Target BPM')}
            sx={{ flex: 1, minWidth: 0 }}
            onValueChange={(value) => {
              if (value !== null) {
                setTargetBpm(value);
              }
            }}
          />

          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <InputLabel id="bpm-tolerance-label">{t('bpmTolerance', 'Tolerance +/-')}</InputLabel>
            <Select
              labelId="bpm-tolerance-label"
              value={bpmTolerance}
              label={t('bpmTolerance', 'Tolerance +/-')}
              onChange={(e) => setBpmTolerance(e.target.value as number)}
            >
              <MenuItem value={2.5}>+/- 2.5 BPM</MenuItem>
              <MenuItem value={5.0}>+/- 5.0 BPM</MenuItem>
              <MenuItem value={7.5}>+/- 7.5 BPM</MenuItem>
              <MenuItem value={10.0}>+/- 10.0 BPM</MenuItem>
              <MenuItem value={12.5}>+/- 12.5 BPM</MenuItem>
              <MenuItem value={15.0}>+/- 15.0 BPM</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}

      <Card>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* <Typography variant="h3" gutterBottom sx={{ fontSize: '1.1rem' }}>
            {t('requestedSongs', 'Requested songs')}
          </Typography> */}

          <Stack spacing={0}>
            {filteredRequests.map((request, index) => (
              <Box
                key={request.id || index}
                draggable={isElectron() && !!request.localTrack?.path}
                onDragStart={(e) => {
                  if (isElectron() && request.localTrack?.path) {
                    const filePath = request.localTrack.path;
                    e.dataTransfer.effectAllowed = 'copy';
                    e.dataTransfer.dropEffect = 'copy';
                    e.preventDefault();
                    setTimeout(() => {
                      window.electron?.startDragFiles([filePath]);
                    }, 0);

                    if (hideOnDrag) {
                      const idx = (request as any).originalIndex;
                      if (typeof idx === 'number') {
                        setTimeout(() => {
                          setHiddenIndices((prev) => {
                            if (!prev.includes(idx)) {
                              if (dynamicAdjustment) {
                                const track = request.localTrack;
                                const rawKey = track?.key;
                                const rawBpm = track?.bpm;
                                const parsed = parseKey(rawKey);

                                const hasKey = !!parsed;
                                const hasBpm = !!rawBpm && rawBpm > 0;

                                if (hasKey || hasBpm) {
                                  setFilterHistory((h) => [
                                    ...h,
                                    { key: targetKey, bpm: targetBpm, adjusted: true },
                                  ]);
                                  if (parsed) setTargetKey(parsed.formatted);
                                  if (rawBpm && rawBpm > 0) setTargetBpm(Math.round(rawBpm));
                                } else {
                                  setFilterHistory((h) => [
                                    ...h,
                                    { key: targetKey, bpm: targetBpm, adjusted: false },
                                  ]);
                                }
                              } else {
                                setFilterHistory((h) => [
                                  ...h,
                                  { key: targetKey, bpm: targetBpm, adjusted: false },
                                ]);
                              }
                              return [...prev, idx];
                            }
                            return prev;
                          });
                        }, 100);
                      }
                    }
                  }
                }}
                sx={{
                  opacity: request.localTrack?.path ? 1 : 0.4,
                  cursor: request.localTrack?.path && isElectron() ? 'grab' : 'default',
                  transition: 'opacity 0.2s',
                  '&:hover': { bgcolor: request.localTrack?.path ? 'action.hover' : 'transparent' },
                }}
              >
                <CompactRequestEntry request={request} />
              </Box>
            ))}
            {filteredRequests.length === 0 && (
              <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                {t('noTracksFound', 'No tracks found...')}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
